import {Box, Typography} from '@mui/material';
import {ReactNode, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {
  IGetAIReviewJobDetailsResponse,
  ISaveProtocolAsNewPayload,
} from '../../../../../api/types/aiReview.type';
import {useSaveProtocolAsNew} from '../../../../../hooks/ai/review/useReview';
import {RootState} from '../../../../../redux/store';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrToastNotification from '../../../../common/arr-toast-notification/ArrToastNotification';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {ToastType} from '../../../../../shared/constants';
import {
  PROTOCOL_VERSION_DISPLAY_MODE,
  getParentProtocolVersionName,
  getProtocolVersionName,
  resolveProtocolVersionMode,
  shouldShowDraftProtocolChangedIcon,
} from '../../common/JobDetails/protocolVersionMode';
import SaveAsNewProtocolModal, {
  getSaveAsNewProtocolErrorMessage,
} from '../../common/JobDetails/SaveAsNewProtocolModal';
import {toCapitalized} from '../../utils';
import './JobResultMetadataRow.scss';

const ISSUE_CODING = 'ISSUECODE';
const RELEVANCY = 'RELEVANCY';

const getAIReviewJobType = (
  type: string | undefined,
  t: (key: string) => string
) => {
  if (!type) {
    return '';
  }

  switch (type) {
    case ISSUE_CODING:
      return t('ai.review.jobType.issueCoding');
    case RELEVANCY:
      return t('ai.review.jobType.relevancy');
    default:
      return type;
  }
};

const formatJobStatus = (
  status: string | undefined,
  t: (key: string) => string
) => {
  if (!status) {
    return '';
  }

  const normalized = status.trim().toUpperCase();
  if (normalized === 'COMPLETED' || normalized === 'COMPLETE') {
    return t('ai.review.jobResult.header.meta.statusComplete');
  }

  return toCapitalized(status);
};

type JobResultMetadataRowProps = {
  jobDetails: IGetAIReviewJobDetailsResponse | null;
  variant?: 'default' | 'issueCoding';
  onRefetchJobDetails?: () => void;
};

const JobResultMetadataRow: React.FC<JobResultMetadataRowProps> = ({
  jobDetails,
  variant = 'default',
  onRefetchJobDetails,
}) => {
  const {t} = useTranslation();
  const userRights = useSelector(
    (state: RootState) => state.actionmenu?.userRights
  );
  const canManageProtocols = Boolean(userRights?.canManageProtocols);
  const [isSaveAsNewModalOpen, setIsSaveAsNewModalOpen] = useState(false);
  const [savedAsProtocolName, setSavedAsProtocolName] = useState<string | null>(
    null
  );
  const [saveAsNewError, setSaveAsNewError] = useState<string | null>(null);
  const [isSaveAsNewSuccessToastOpen, setIsSaveAsNewSuccessToastOpen] =
    useState(false);
  const {mutate: saveProtocolAsNew} = useSaveProtocolAsNew();

  const displayMode = resolveProtocolVersionMode(jobDetails);
  const showDraftChangedIcon = shouldShowDraftProtocolChangedIcon(jobDetails);
  const protocolVersionName = getProtocolVersionName(jobDetails);
  const parentProtocolVersionName = getParentProtocolVersionName(jobDetails);
  const displayedSavedAsName =
    protocolVersionName || savedAsProtocolName?.trim() || '';
  const protocolChangedTooltip = t(
    'ai.review.jobDetailsTab.draftProtocolChangedTooltip'
  );
  const modifiedArtifactsTooltip = t(
    'ai.review.jobDetailsTab.modifiedArtifactsTooltip'
  );

  const showSavedModifiedLayout =
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVED ||
    Boolean(savedAsProtocolName);
  const showSaveAsNewButton =
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVE_AS_NEW &&
    !savedAsProtocolName;

  const handleSaveAsNewProtocol = () => {
    if (!canManageProtocols) {
      return;
    }
    setSaveAsNewError(null);
    setIsSaveAsNewModalOpen(true);
  };

  const handleCloseSaveAsNewModal = () => {
    setSaveAsNewError(null);
    setIsSaveAsNewModalOpen(false);
  };

  const handleSaveAsNewProtocolConfirm = (fileName: string) => {
    if (!jobDetails) {
      return;
    }

    const previousProtocolId =
      jobDetails.reviewProtocolID ?? jobDetails.parentProtocolID;
    const protocolId = Number(previousProtocolId);

    if (previousProtocolId == null || !Number.isFinite(protocolId)) {
      return;
    }

    const payload: ISaveProtocolAsNewPayload = {
      protocolId,
      protocolName: fileName,
    };

    setSaveAsNewError(null);
    saveProtocolAsNew(payload, {
      onSuccess: () => {
        setSavedAsProtocolName(fileName);
        setSaveAsNewError(null);
        setIsSaveAsNewModalOpen(false);
        setIsSaveAsNewSuccessToastOpen(true);
        onRefetchJobDetails?.();
      },
      onError: (error) => {
        setSaveAsNewError(getSaveAsNewProtocolErrorMessage(error));
      },
    });
  };

  const renderDraftChangedIcon = () =>
    showDraftChangedIcon ? (
      <ArrTooltipWrapper
        trigger={
          <InfoIcon
            className="job-result-metadata-protocol-changed-icon"
            data-testid="job-result-protocol-changed-info-icon"
            aria-hidden="true"
            role="button"
            tabIndex={0}
            aria-label={protocolChangedTooltip}
          />
        }
        title={protocolChangedTooltip}
      >
        {protocolChangedTooltip}
      </ArrTooltipWrapper>
    ) : null;

  const renderModifiedMeta = () => (
    <Box
      className="job-result-metadata-protocol-modified-meta"
      data-testid="job-result-protocol-modified-meta"
    >
      <Typography
        component="span"
        className="typo-text label3_size job-result-metadata-protocol-modified-label"
        data-testid="job-result-protocol-modified-label"
      >
        {t('ai.review.jobDetailsTab.modifiedProtocol')}
      </Typography>
      <ArrTooltipWrapper
        trigger={
          <InfoIcon
            className="job-result-metadata-protocol-modified-icon"
            data-testid="job-result-protocol-modified-info-icon"
            aria-hidden="true"
            role="button"
            tabIndex={0}
            aria-label={modifiedArtifactsTooltip}
          />
        }
        title={modifiedArtifactsTooltip}
      >
        {modifiedArtifactsTooltip}
      </ArrTooltipWrapper>
    </Box>
  );

  const renderSaveAsNewButton = () =>
    showSaveAsNewButton ? (
      <ArrButton
        variant="primary"
        size="xs"
        disabled={!canManageProtocols}
        dataTestId="job-result-protocol-save-as-new-button"
        onClick={handleSaveAsNewProtocol}
        className="job-result-metadata-save-as-new-btn"
      >
        {t('ai.review.jobDetailsTab.saveAsNewProtocol')}
      </ArrButton>
    ) : null;

  const renderProtocolName = (name: string, testId: string) => (
    <Typography
      component="span"
      className="typo-text label2_size job-result-metadata-protocol-name"
      data-testid={testId}
    >
      {name}
    </Typography>
  );

  // Figma 16656:131095 — Protocol value is a single row: name + Modified [i]
  // (+ Save as New when unsaved). Saved As is a separate metadata column.
  const renderProtocolValue = (): ReactNode => {
    if (displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MANUAL) {
      return t('ai.review.jobDetailsTab.manualProtocol');
    }

    if (
      displayMode === PROTOCOL_VERSION_DISPLAY_MODE.MODIFIED_SAVE_AS_NEW ||
      showSavedModifiedLayout
    ) {
      return (
        <Box
          className="job-result-metadata-protocol"
          data-testid={
            showSavedModifiedLayout
              ? 'job-result-protocol-modified-saved'
              : 'job-result-protocol-modified'
          }
        >
          {parentProtocolVersionName
            ? renderProtocolName(
                parentProtocolVersionName,
                'job-result-protocol-name'
              )
            : null}
          {renderModifiedMeta()}
          {renderSaveAsNewButton()}
        </Box>
      );
    }

    return (
      <>
        {protocolVersionName}
        {renderDraftChangedIcon()}
      </>
    );
  };

  const isProtocolActionTone =
    displayMode === PROTOCOL_VERSION_DISPLAY_MODE.PROTOCOL_NAME;

  const metadataItems: Array<{
    label: string;
    value: ReactNode;
    valueTone?: 'action';
    isProtocol?: boolean;
  }> = [
    {
      label: t('ai.review.jobResult.header.meta.jobId'),
      value: jobDetails?.jobId ?? '',
    },
    {
      label: t('ai.review.jobResult.header.meta.type'),
      value: getAIReviewJobType(jobDetails?.jobType, t),
    },
    {
      label: t('ai.review.jobResult.header.meta.createdBy'),
      value: jobDetails?.createdBy?.trim() ?? '',
    },
    {
      label: t('ai.review.jobResult.header.meta.status'),
      value: formatJobStatus(jobDetails?.status, t),
    },
    {
      label: t('ai.review.jobResult.header.meta.source'),
      value:
        jobDetails?.sourceType && jobDetails?.sourceName
          ? `${jobDetails.sourceType} - ${jobDetails.sourceName}`
          : '',
    },
    {
      label: t('ai.review.jobResult.header.meta.protocol'),
      value: renderProtocolValue(),
      valueTone: isProtocolActionTone ? 'action' : undefined,
      isProtocol: true,
    },
    // Figma: Saved As is its own column beside Protocol
    ...(showSavedModifiedLayout && displayedSavedAsName
      ? [
          {
            label: t('ai.review.jobDetailsTab.savedAs'),
            value: (
              <Typography
                component="span"
                className="typo-text label2_size job-result-metadata-protocol-name"
                data-testid="job-result-protocol-saved-as-name"
              >
                {displayedSavedAsName}
              </Typography>
            ),
            valueTone: 'action' as const,
          },
        ]
      : []),
  ];

  return (
    <>
      <SaveAsNewProtocolModal
        open={isSaveAsNewModalOpen}
        onClose={handleCloseSaveAsNewModal}
        onSave={handleSaveAsNewProtocolConfirm}
        errorMessage={saveAsNewError}
      />
      {isSaveAsNewSuccessToastOpen && (
        <ArrToastNotification
          message={t('ai.review.protocolIntelligence.saveAsNewSuccessToast')}
          status={[ToastType.Success]}
          onClose={() => setIsSaveAsNewSuccessToastOpen(false)}
        />
      )}
      <Box
        className={`job-result-metadata-row${
          variant === 'issueCoding'
            ? ' job-result-metadata-row--issue-coding'
            : ''
        }`}
        data-testid="job-result-metadata-row"
      >
        {metadataItems.map(({label, value, valueTone, isProtocol}, index) => (
          <Box
            className="job-result-metadata-item"
            key={`${label}-${index}`}
            tabIndex={0}
            data-testid="job-result-metadata-item"
          >
            <Typography
              className="typo-text label3_size job-result-metadata-label"
              data-testid={`job-result-metadata-label-${index}`}
            >
              {label}
            </Typography>
            <Box
              className={`typo-text label3_size job-result-metadata-value${
                valueTone === 'action'
                  ? ' job-result-metadata-value--action'
                  : ''
              }${isProtocol ? ' job-result-metadata-value--protocol' : ''}`}
              data-testid={`job-result-metadata-value-${index}`}
            >
              {value}
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
};

export default JobResultMetadataRow;
